/* Unified Inbox - one triaged place for every inbound conversation, wired to accounts, opportunities, Sales Intelligence and the AI Workspace. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Inbox as InboxIcon } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { EmptyState } from "@/components/common";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { ThreadList } from "@/components/inbox/ThreadList";
import { Conversation } from "@/components/inbox/Conversation";
import { ContextPanel } from "@/components/inbox/ContextPanel";

export const notifyInboxChanged = () => window.dispatchEvent(new Event("inbox:changed"));

function useMedia(query) {
  const [m, setM] = useState(() => (typeof window !== "undefined" ? window.matchMedia(query).matches : true));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = (e) => setM(e.matches);
    setM(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return m;
}

export default function InboxPage() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const isDesktop = useMedia("(min-width: 1024px)");
  const wide = useMedia("(min-width: 1400px)");

  const [view, setView] = useState("focus");
  const [channel, setChannel] = useState(null);
  const [q, setQ] = useState("");
  const [threads, setThreads] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [contextOpen, setContextOpen] = useState(true);
  const [contextSheet, setContextSheet] = useState(false);
  const [busy, setBusy] = useState(false);
  const qRef = useRef(q);
  qRef.current = q;

  const loadSummary = useCallback(() => api.get("/inbox/summary").then((r) => setSummary(r.data)).catch(() => {}), []);

  const loadList = useCallback(() => {
    setListError(null);
    setListLoading(true);
    const params = { view };
    if (channel) params.channel = channel;
    if (qRef.current.trim()) params.q = qRef.current.trim();
    return api
      .get("/inbox/threads", { params })
      .then((r) => setThreads(r.data.threads))
      .catch((e) => setListError(errMsg(e)))
      .finally(() => setListLoading(false));
  }, [view, channel]);

  useEffect(() => {
    loadSummary();
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, [loadSummary]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  /* debounced search */
  useEffect(() => {
    const h = setTimeout(() => loadList(), 220);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  /* auto-select the first thread on desktop */
  useEffect(() => {
    if (isDesktop && !threadId && threads && threads.length) navigate(`/inbox/${threads[0].id}`, { replace: true });
  }, [isDesktop, threadId, threads, navigate]);

  const loadDetail = useCallback(
    (id) => {
      if (!id) return;
      setDetailError(null);
      setDetailLoading(true);
      api
        .get(`/inbox/threads/${id}`)
        .then((r) => {
          setDetail(r.data);
          if (r.data.thread?.unread) {
            api.patch(`/inbox/threads/${id}`, { unread: false }).then(() => {
              setThreads((ts) => (ts || []).map((t) => (t.id === id ? { ...t, unread: false } : t)));
              loadSummary();
              notifyInboxChanged();
            });
          }
        })
        .catch((e) => setDetailError(errMsg(e)))
        .finally(() => setDetailLoading(false));
    },
    [loadSummary]
  );

  useEffect(() => {
    if (threadId) loadDetail(threadId);
    else setDetail(null);
  }, [threadId, loadDetail]);

  const select = (id) => navigate(`/inbox/${id}`);

  const patch = async (body) => {
    if (!threadId) return;
    try {
      const r = await api.patch(`/inbox/threads/${threadId}`, body);
      const t = r.data;
      setDetail((d) => (d ? { ...d, thread: { ...d.thread, ...t } } : d));
      const leaves = (body.status && body.status !== (view === "snoozed" ? "snoozed" : view === "done" ? "done" : "open")) || (body.clear_assignee && view === "mine") || (body.assignee_id && view === "unassigned");
      if (leaves) {
        const idx = (threads || []).findIndex((x) => x.id === threadId);
        const rest = (threads || []).filter((x) => x.id !== threadId);
        setThreads(rest);
        if (body.status === "done") toast.success("Marked as done", { action: { label: "Undo", onClick: () => api.patch(`/inbox/threads/${threadId}`, { status: "open" }).then(() => { loadList(); loadSummary(); notifyInboxChanged(); }) } });
        if (body.status === "snoozed") toast.success(`Snoozed for ${body.snooze_days} day${body.snooze_days > 1 ? "s" : ""}`);
        const next = rest[Math.min(idx, rest.length - 1)];
        if (isDesktop && next) navigate(`/inbox/${next.id}`, { replace: true });
        else if (isDesktop) navigate("/inbox", { replace: true });
      } else {
        setThreads((ts) => (ts || []).map((x) => (x.id === threadId ? { ...x, ...t } : x)));
      }
      loadSummary();
      notifyInboxChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const send = async (body, kind) => {
    const r = await api.post(`/inbox/threads/${threadId}/messages`, { body, kind });
    setDetail((d) => (d ? { ...d, messages: [...d.messages, r.data], thread: { ...d.thread, message_count: (d.thread.message_count || 0) + 1, last_message_at: r.data.sent_at } } : d));
    setThreads((ts) => (ts || []).map((x) => (x.id === threadId ? { ...x, message_count: (x.message_count || 0) + 1, last_message_at: r.data.sent_at } : x)));
  };

  const draft = async (tone, instruction) => {
    const r = await api.post(`/inbox/threads/${threadId}/draft`, { tone, instruction: instruction || null });
    return r.data;
  };

  const createOpportunity = async () => {
    if (!threadId || busy) return null;
    setBusy(true);
    try {
      const r = await api.post(`/inbox/threads/${threadId}/opportunity`);
      if (r.data.created) toast.success("Opportunity created", { description: "Requirements from the AI brief were added.", action: { label: "Open", onClick: () => navigate(`/opportunities/${r.data.opportunity_id}`) } });
      loadDetail(threadId);
      return r.data;
    } catch (e) {
      toast.error(errMsg(e));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const startWorkspace = async () => {
    if (!threadId || busy) return;
    setBusy(true);
    try {
      let oppId = detail?.context?.opportunity?.id || detail?.thread?.opportunity_id || null;
      if (!oppId && detail?.thread?.ai?.intent !== "internal") {
        const r = await api.post(`/inbox/threads/${threadId}/opportunity`);
        oppId = r.data.opportunity_id;
      }
      const ws = await api.post("/workspace", { opportunity_id: oppId });
      const sid = ws.data?.session?.id || ws.data?.id;
      if (!sid) throw new Error("No workspace session");
      navigate(`/workspace/${sid}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  /* keyboard: j / k move, e done */
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || /input|textarea|select/i.test(e.target.tagName) || e.target.isContentEditable) return;
      if (!threads?.length) return;
      const idx = threads.findIndex((t) => t.id === threadId);
      if (e.key === "j" || e.key === "ArrowDown") {
        e.preventDefault();
        const n = threads[Math.min(idx + 1, threads.length - 1)];
        if (n) navigate(`/inbox/${n.id}`);
      } else if (e.key === "k" || e.key === "ArrowUp") {
        e.preventDefault();
        const n = threads[Math.max(idx - 1, 0)];
        if (n) navigate(`/inbox/${n.id}`);
      } else if (e.key === "e" && threadId && detail?.thread?.status === "open") {
        e.preventDefault();
        patch({ status: "done" });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads, threadId, detail]);

  const showContextInline = isDesktop && wide && contextOpen;
  const listVisible = isDesktop || !threadId;
  const convoVisible = isDesktop || Boolean(threadId);
  const usersForAssign = useMemo(() => users.filter((u) => u.id), [users]);

  return (
    <div className="flex h-full min-h-0 w-full bg-[hsl(var(--background))]" data-testid="inbox-page">
      {listVisible ? (
        <ThreadList
          className={cn("w-full shrink-0 border-r border-black/[0.06] bg-[hsl(var(--sidebar)/0.6)] lg:w-[360px] xl:w-[400px]")}
          threads={threads}
          loading={listLoading}
          error={listError}
          onRetry={loadList}
          activeId={threadId}
          onSelect={select}
          view={view}
          onView={setView}
          channel={channel}
          onChannel={setChannel}
          q={q}
          onQ={setQ}
          summary={summary}
        />
      ) : null}
      {convoVisible ? (
        <div className="flex min-w-0 flex-1">
          <div className="min-w-0 flex-1">
            {threadId ? (
              <Conversation
                data={detail}
                loading={detailLoading && !detail}
                error={detailError}
                onRetry={() => loadDetail(threadId)}
                users={usersForAssign}
                onPatch={patch}
                onSend={send}
                onDraft={draft}
                onCreateOpportunity={createOpportunity}
                onStartWorkspace={startWorkspace}
                onBack={!isDesktop ? () => navigate("/inbox") : null}
                onToggleContext={() => (wide ? setContextOpen((v) => !v) : setContextSheet(true))}
                contextOpen={showContextInline}
                busy={busy}
              />
            ) : (
              <div className="flex h-full items-center justify-center p-10">
                <EmptyState icon={InboxIcon} title="Select a conversation" description="Pick a thread on the left, or press j / k to move through the list." testId="inbox-no-selection" />
              </div>
            )}
          </div>
          {showContextInline && threadId ? <ContextPanel data={detail} users={users} onCreateOpportunity={createOpportunity} busy={busy} className="w-[320px] shrink-0 border-l border-black/[0.06] 2xl:w-[340px]" /> : null}
        </div>
      ) : null}
      <Sheet open={contextSheet} onOpenChange={setContextSheet}>
        <SheetContent side="right" className="w-[min(92vw,380px)] p-0 sm:max-w-none" data-testid="inbox-context-sheet">
          <SheetTitle className="sr-only">Context</SheetTitle>
          <ContextPanel data={detail} users={users} onCreateOpportunity={createOpportunity} busy={busy} className="h-full" />
        </SheetContent>
      </Sheet>
    </div>
  );
}
